import React, { useState } from "react";
import Navbar from "./Navbar.js";
import useAuth from "../hooks/useAuth.js";
import CallToAction from "./CallToAction.js";
import Footer from "./Footer.js";
import FAQ from "../components/FAQ.js";

export default function FAQs() {
  const { isAuthenticated } = useAuth();

  const [faqs, setFaqs] = useState([
    {
      question: "What is Ajo App?",
      answer:
        "Ajo App is a digital platform that modernizes traditional thrift contribution methods. It allows users to save money in groups, automate contributions, and receive lump-sum payouts in a secure and organized manner.",
      open: true
    },
    {
      question: "Who can use Ajo App?",
      answer: "Anyone aged 18 and above with a valid email address and a bank account can use Ajo App. It’s ideal for individuals, families, and small businesses looking to save collaboratively.",
      open: false
    },
    {
      question: "How does Ajo App work?",
      answer: "Ajo App facilitates group savings by: <ul><li>Allowing users to create or join thrift groups.</li><li>Automating contributions on a set schedule (weekly, bi-weekly, or monthly).</li><li>Distributing lump-sum payouts to each group member in a predefined order.</li></ul>",
      open: false
    },
    {
      question: "How do I sign up for Ajo App?",
      answer: "To sign up: <ul><li>Visit www.ajoapp.ca from your browser.</li><li>Create an account using your email address or phone number.</li><li>Link your bank account securely.</li></ul>",
      open: false
    },
    {
      question: "Can I create my own thrift group?",
      answer: "Yes, you can create a thrift group by naming it, specifying the number of members, setting contribution amounts, and choosing the payout order.",
      open: false
    },
    {
      question: "Can I join multiple thrift groups?",
      answer: "Yes, you can join multiple thrift groups, provided you meet the contribution requirements for each group.",
      open: false
    },
    {
      question: "How are contributions made?",
      answer: "Contributions are automated. Once you link your bank account, the app will deduct the agreed contribution amount based on the group’s schedule.",
      open: false
    },
    {
      question: "What happens if I miss a contribution?",
      answer: "If you miss a contribution: <ul><li>You will receive a notification to make the payment manually.</li><li>Repeated missed contributions may result in removal from the group and loss of eligibility for payouts.</li></ul>",
      open: false
    },
    {
      question: "How are payouts distributed?",
      answer: "Payouts are sent to the designated group member according to the predefined order. Funds are deposited directly into the recipient’s linked bank account.",
      open: false
    },
    {
      question: "Is there a fee for using Ajo App?",
      answer: "Yes, Ajo App charges a small transaction fee to cover operational costs. The fee structure is transparent and will be displayed during group creation.",
      open: false
    },
    {
      question: "What is Group Funding?",
      answer: "Group Funding is a feature that allows communities to pool resources together to fund a specific goal or project. Unlike rotating thrift savings, all contributions go toward a common purpose, such as supporting a member in need or funding a community project.",
      open: false
    },
    {
      question: "How is Group Funding different from regular thrift groups?",
      answer: "In regular thrift groups, members take turns receiving payouts. In Group Funding, all contributions are pooled together and disbursed to achieve a specific goal or to support a designated beneficiary. There's no rotation - it's a one-time collective effort.",
      open: false
    },
    {
      question: "How do I create a Group Funding campaign?",
      answer: "To create a Group Funding campaign: <ul><li>Navigate to the Group Funding section in your dashboard.</li><li>Click 'Create Campaign' and provide details like the campaign name, goal amount, and purpose.</li><li>Set the contribution deadline and invite members to participate.</li></ul>",
      open: false
    },
    {
      question: "Can anyone contribute to a Group Funding campaign?",
      answer: "Yes, anyone invited to the campaign can contribute. You can also make campaigns public to allow contributions from anyone within the Ajo App community.",
      open: false
    },
    {
      question: "How is the collected money disbursed in Group Funding?",
      answer: "Once the funding goal is reached or the deadline passes, the campaign creator can initiate disbursement. Funds are transferred directly to the designated beneficiary's linked bank account or used according to the campaign's stated purpose.",
      open: false
    },
    {
      question: "Can I track contributions in a Group Funding campaign?",
      answer: "Yes, all contributors can see real-time updates on the total amount raised, number of contributors, and progress toward the funding goal through the campaign dashboard.",
      open: false
    },
    {
      question: "What are the two types of Group Funding on Ajo App?",
      answer: "Ajo App supports two types of Group Funding: <ul><li><strong>Regular Group Funding</strong>, which follows a scheduled contribution cycle.</li><li><strong>Flexi Group Funding</strong>, which allows members to contribute manually at their own pace toward a shared target.</li></ul>",
      open: false
    },
    {
      question: "Does Flexi Group Funding use automatic debit?",
      answer: "No. Flexi Group Funding is designed for manual contributions. Members choose when to pay, while regular scheduled group payments continue to follow the automated contribution cycle when applicable.",
      open: false
    },
    {
      question: "How much do I pay in a Flexi Group Funding campaign?",
      answer: "In a Flexi Group Funding campaign, your payment is based on the contribution target set for the group. You can contribute toward that target manually, as long as your payment meets the minimum allowed amount for the campaign.",
      open: false
    },
    {
      question: "Can members see their contributions in Group Funding?",
      answer: "Yes. Members can view their own contribution activity and track how much they have paid toward the campaign directly from the Group Funding details page.",
      open: false
    },
    {
      question: "When is a Group Funding campaign marked as completed?",
      answer: "A Group Funding campaign is typically marked as completed when the contribution target has been reached or when the campaign is otherwise closed by its creator or administrator based on the group goal.",
      open: false
    },
    {
      question: "Are there fees for Group Funding campaigns?",
      answer: "Yes, a small service fee applies to Group Funding campaigns to cover platform costs. The fee structure is clearly displayed when creating a campaign.",
      open: false
    },
    {
      question: "Is my financial information secure?",
      answer: "Absolutely. Ajo App uses Stripe for secure financial transactions and complies with industry-standard security protocols, including encryption and PCI compliance.",
      open: false
    },
    {
      question: "Does Ajo App share my personal information?",
      answer: "No. Your personal information is only used to provide services and is not shared with third parties without your consent. For more details, refer to our Privacy Policy.",
      open: false
    },
    {
      question: "How do I reset my password?",
      answer: "To reset your password: <ul><li>Click “Forgot Password” on the login screen.</li><li>Enter your email address or phone number.</li><li>Follow the instructions sent to reset your password.</li></ul>",
      open: false
    },
    {
      question: "How do I link my bank account?",
      answer: "Go to the 'Profile' section in the app. Click on 'Link Bank Account.' Follow the steps to securely connect your bank account.",
      open: false
    },
    {
      question: "Can I delete my account?",
      answer: "Yes, you can delete your account by contacting our support team at support@ajoapp.ca. Note that deleting your account will terminate your participation in thrift groups.",
      open: false
    },
    {
      question: "Can I change the payout order after the group starts?",
      answer: "No, the payout order is fixed once the group begins to ensure fairness for all members.",
      open: false
    },
    {
      question: "Can I leave a thrift group before it’s completed?",
      answer: "Leaving a group mid-cycle is not recommended, as it disrupts the schedule. If you must leave, contact your group admin to discuss terms.",
      open: false
    },
    {
      question: "What happens if a group member doesn’t contribute?",
      answer: "If a member fails to contribute, the group admin can: <ul><li>Pause the group cycle until the contribution is resolved.</li><li>Remove the member and adjust the payout schedule.</li></ul>",
      open: false
    },
    {
      question: "Why is my contribution failing?",
      answer: "Contribution failures may occur due to: <ul><li>Insufficient funds in your linked bank account.</li><li>Issues with bank account verification.</li></ul> Contact our support team for assistance.",
      open: false
    },
    {
      question: "I didn't receive my payout. What should I do?",
      answer: "First, check your bank account for delays. If the issue persists, contact our support team immediately at support@ajoapp.ca.",
      open: false
    },
    {
      question: "How can I contact Ajo App support?",
      answer: "You can reach us via: <ul><li>Email: support@ajoapp.ca</li><li>In-App Support: Navigate to the 'Help' section.</li></ul>",
      open: false
    },
    {
      question: "Where can I find updates about Ajo App?",
      answer: "Stay updated via: <ul><li>Notifications in the app.</li><li>Our social media channels: @ajoapp_ca.</li></ul>",
      open: false
    },
    {
      question: "Can I suggest features for the Ajo App?",
      answer: "Yes! We value your feedback. Share your suggestions via the 'Feedback' section in the app or email us at info@ajoapp.ca.",
      open: false
    }
  ]);

  const toggleFAQ = index => {
    setFaqs(
      faqs.map((faq, i) => {
        if (i === index) {
          faq.open = !faq.open;
        } else {
          faq.open = false;
        }

        return faq;
      })
    );
  };

  return (
    <div className="FAQ">
      <Navbar isAuthenticated={isAuthenticated} />
      <div className="container-fluid text-white pl-16 py-5 page-header mb-5">
        <h1 className="text-3xl font-bold mb-4">Frequently asked Questions</h1>
        <p className="text-sm mb-2">Effective Date: 27/01/2025</p>
        <p className="text-sm">Last Updated: 19/07/2026</p>
      </div>
      <div className="faqs">
        {faqs.map((faq, index) => (
          <FAQ faq={faq} index={index} key={index} toggleFAQ={toggleFAQ} />
        ))}
      </div>
      <CallToAction />
      <Footer />
    </div>
  );
}